import { motion } from 'framer-motion';
import { 
  User, 
  Ship, 
  Globe, 
  Calendar, 
  Award, 
  FileCheck,
  Clock,
  ChevronRight
} from 'lucide-react';
import type { CrewMember } from '../types';

interface CrewCardProps {
  crew: CrewMember;
  index: number;
  onViewDetails?: (crew: CrewMember) => void;
}

function ScoreRing({ score }: { score: number }) {
  const circumference = 2 * Math.PI * 32;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  const getScoreColor = (score: number) => {
    if (score >= 90) return '#22c55e'; // green
    if (score >= 70) return '#3b82f6'; // blue
    if (score >= 50) return '#f59e0b'; // amber
    return '#ef4444'; // red
  };

  return (
    <div className="score-ring w-16 h-16 flex-shrink-0">
      <svg width="64" height="64" viewBox="0 0 64 64">
        {/* Background circle */}
        <circle
          cx="32"
          cy="32"
          r="28"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="4"
        />
        {/* Progress circle */}
        <circle
          cx="32"
          cy="32"
          r="28"
          fill="none"
          stroke={getScoreColor(score)}
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000 ease-out"
          style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-semibold text-gray-900">{score}%</span>
      </div>
    </div>
  );
}

export function CrewCard({ crew, index, onViewDetails }: CrewCardProps) {
  const score = crew.match_score ?? 100;
  const dueDate = crew.due_soff_date || crew.due_date;
  const formattedDate = dueDate 
    ? new Date(dueDate).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      })
    : 'N/A';

  const isAvailableSoon = dueDate && 
    new Date(dueDate) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  
  // Get display name (combine name and surname if both exist)
  const displayName = crew.surname 
    ? `${crew.name || ''} ${crew.surname}`.trim() 
    : crew.name || 'Unknown';
  
  // Get vessel type for badge
  const vesselType = crew.vessel_type || crew.crew_type || '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.03 }}
      className="crew-card group"
    >
      <div className="flex gap-4">
        {/* Score Ring */}
        <ScoreRing score={score} />

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {displayName}
              </h3>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  {crew.cid}
                </span>
                <span className="flex items-center gap-1">
                  <Award className="w-3.5 h-3.5" />
                  {crew.rank}
                </span>
                <span className="flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5" />
                  {crew.nationality}
                </span>
                {crew.vessel && (
                  <span className="flex items-center gap-1">
                    <Ship className="w-3.5 h-3.5" />
                    {crew.vessel}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-3">
            {crew.experience_months !== undefined && crew.experience_months > 0 && (
              <span className="badge-info">
                <Clock className="w-3 h-3 mr-1" />
                {crew.experience_months} months
              </span>
            )}
            {crew.contracts !== undefined && crew.contracts > 0 && (
              <span className="badge-default">
                <FileCheck className="w-3 h-3 mr-1" />
                {crew.contracts} contracts
              </span>
            )}
            {isAvailableSoon && (
              <span className="badge-success">
                Available soon
              </span>
            )}
            {vesselType && vesselType.toLowerCase().includes('large') && (
              <span className="badge-warning">
                {vesselType.toLowerCase().includes('very') ? 'VLV' : 'Large'} Experience
              </span>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <Calendar className="w-4 h-4" />
              <span>Due: {formattedDate}</span>
            </div>
            <button
              onClick={() => onViewDetails?.(crew)}
              className="flex items-center gap-1 text-sm font-medium text-primary-600 
                       hover:text-primary-700 transition-colors group/btn"
            >
              View Details
              <ChevronRight className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
